import { useState } from 'react';
import { Hand, X, Loader2 } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useLocation } from '@context/LocationContext';
import { getCategoryInfo } from '@utils/helpers';
import api from '@utils/api';
import toast from 'react-hot-toast';

const TYPES = ['sports', 'fitness', 'learning', 'social', 'outdoors', 'gaming', 'food'];

const PostIntentButton = () => {
  const { user } = useAuth();
  const { location } = useLocation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const handlePost = async () => {
    if (!selected) return;
    try {
      setLoading(true);
      await api.post('/intents', {
        activityType: selected,
        coordinates: [location.lng, location.lat],
      });
      toast.success('People nearby can see you!');
      setOpen(false);
      setSelected('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-xl bg-violet-50 border border-violet-200/80 px-3.5 py-2 text-xs font-semibold text-violet-600 transition-all hover:bg-violet-100 dark:bg-violet-500/10 dark:border-violet-500/20 dark:text-violet-400 dark:hover:bg-violet-500/15"
      >
        <Hand size={14} />
        I'm Looking...
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-sm rounded-3xl bg-white dark:bg-surface-900 shadow-2xl overflow-hidden animate-scale-in">
            <div className="flex items-center justify-between px-5 pt-5 pb-2">
              <h3 className="text-lg font-extrabold text-surface-900 dark:text-white">What are you looking for?</h3>
              <button onClick={() => setOpen(false)} className="theme-toggle"><X size={16} /></button>
            </div>
            <div className="px-5 pb-5 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {TYPES.map((t) => {
                  const cat = getCategoryInfo(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSelected(t)}
                      className={`flex flex-col items-center gap-1 rounded-2xl py-3 px-2 transition-all duration-200 border ${
                        selected === t
                          ? 'bg-violet-50 border-violet-400 ring-2 ring-violet-500/20 dark:bg-violet-500/10 dark:border-violet-400'
                          : 'border-surface-200/80 bg-surface-50 hover:border-violet-200 dark:bg-surface-800/60 dark:border-surface-700/50'
                      }`}
                    >
                      <span className="text-xl">{cat.emoji}</span>
                      <span className="text-[11px] font-semibold text-surface-700 dark:text-surface-300">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={handlePost}
                disabled={loading || !selected}
                className="w-full btn-primary !py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : 'Post Intent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostIntentButton;
